import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { joinClasses } from 'components/utils/react_helper';
import I18n from '~/utils/translations';
import tinycolor from 'tinycolor2';

export default class PropertyMap extends Component {
  constructor(props) {
    super(props);

    this.mapDiv = null;
    this.map = null;
    this.streetView = null;
    this.state = {
      loaded: false,
      currentView: 'map',
    };
  }

  componentDidMount() {
    this.setState({ loaded: true });
    this.initialiseMap();
  }

  get propertyPosition() {
    return {
      lat: this.props.lat,
      lng: this.props.lon,
    };
  }

  render() {
    return (
      <Fragment>
        {this.renderNav()}
        <div ref={(mapDiv) => (this.mapDiv = mapDiv)} className="home-map__container"></div>
      </Fragment>
    );
  }

  renderNav = () => {
    const tabs = [
      {
        id: 'map',
        text: I18n.t('pages.homes.map.map_view_tab'),
        onClick: this.showMapView,
      },
      {
        id: 'streetview',
        text: I18n.t('pages.homes.map.street_view_tab'),
        onClick: this.showStreetView,
      },
    ];

    return (
      <ul className="home-map__nav">
        {tabs.map((tab) => (
          <li key={tab.id} className={this.navItemClasses(tab.id)}>
            <button className="home-map__nav-link" onClick={tab.onClick}>
              {tab.text}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  navItemClasses = (tabId) => {
    return joinClasses(['home-map__nav-item', this.state.currentView == tabId && 'home-map__nav-item--active']);
  };

  initialiseMap = () => {
    this.map = new google.maps.Map(this.mapDiv, {
      mapId: this.props.mapID,
      center: this.propertyPosition,
      zoom: 17,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      rotateControl: true,
      fullscreenControl: true,
      styles: MAPSTYLES,
    });

    this.streetView = this.map.getStreetView();
    this.streetView.setPosition(this.propertyPosition);
    this.streetView.setOptions({ addressControl: false, enableCloseButton: false });

    const darker = this.props.color ? tinycolor(this.props.color).darken().toString() : null;

    const pin = new google.maps.marker.PinElement({
      scale: 1.5,
      background: this.props.color,
      borderColor: darker,
      glyphColor: darker,
    });

    const icon = document.createElement('img');
    icon.src = 'https://s3.eu-west-2.amazonaws.com/resident.ly-production-static-assets/home-pin-2021.svg';

    new google.maps.marker.AdvancedMarkerElement({
      position: new google.maps.LatLng(this.props.lat, this.props.lon),
      map: this.map,
      content: this.props.color ? pin.element : icon,
    });
  };

  showMapView = () => {
    this.resetMapView();
    this.setState({ currentView: 'map' });
    this.streetView.setVisible(false);
  };

  showStreetView = () => {
    this.resetStreetView();
    this.setState({ currentView: 'streetview' });
    this.streetView.setVisible(true);
    track_event('street-view-tab', 'open');
  };

  resetMapView = () => {
    this.map.setCenter(this.propertyPosition);
    this.map.setZoom(17);
  };

  resetStreetView = () => {
    this.streetView.setPosition(this.propertyPosition);
    this.streetView.setPov({ heading: 0, pitch: 0, zoom: 1 });
  };
}

PropertyMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  lat: PropTypes.number.isRequired,
  lon: PropTypes.number.isRequired,
};

const MAPSTYLES = [
  {
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5',
      },
    ],
  },
  {
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161',
      },
    ],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#f5f5f5',
      },
    ],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#bdbdbd',
      },
    ],
  },
  {
    featureType: 'poi',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#e1f4c3',
      },
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#757575',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dadada',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#616161',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e',
      },
    ],
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e5e5e5',
      },
    ],
  },
  {
    featureType: 'transit.station',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        color: '#eeeeee',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#c9c9c9',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#c3e0f4',
      },
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#9e9e9e',
      },
    ],
  },
];
